<template>
  <div>
    <confirmation-modal
      :confirm-text="$t('template-builder.leave-page')"
      :decline-text="$t('common.stay-on-page')"
      :active="isConfirmationModalOpen"
      @accepted="onConfirmLeavePage"
      @declined="onDeclineLeavePage"
    >
      {{ $t('common.lose-changes-on-back') }}
    </confirmation-modal>

    <div class="navbar-template-builder big:px-view px-5" style="z-index: 19;">
      <ul class="nav h-full" style="padding-left: 0;">
        <li class="relative navbar-template-builder__back-container" @click="onBackClick">
          <icon icon="arrow-left" />
        </li>
        <li class="title">
          {{ $t('template-builder.title') }}
        </li>
      </ul>
    </div>

    <div class="navbar-template-builder navbar-template-builder--second big:px-view px-5" style="z-index: 19;">
      <ul class="nav" style="padding-left: 0;">
        <li
          v-for="(item, index) in menu"
          :key="index"
          class="big:block step relative mr-10"
          :class="{ 'cursor-pointer': !isTranslationMode,'cursor-default': isTranslationMode }"
        >
          <a
            :class="{'router-link-active': activeStage === index, 'cursor-default': isTranslationMode}"
            @click="changeStage(index)"
          >
            <span>
              <starred-item :is-starred="item.starred">
                <span class="flex items-center">
                  <span :class="{ 'hover-primary': !isTranslationMode }">
                    {{ index + 1 }}. {{ item.title }}
                  </span>
                </span>
              </starred-item>
            </span>
          </a>
          <font-awesome-icon
            v-if="index + 1 < menu.length"
            :icon="['far', 'chevron-right']"
            class="text-80 absolute next-step"
          />
        </li>

        <li class="big:block ml-auto"></li>

        <template v-if="onlyStartInspection">
          <li class="big:block" style="padding-top: 0.5rem">
            <progress-button
              type="button"
              :disabled="isWorking"
              :processing="submitted"
              @click.native="handleSave(10)"
            >
              {{ $t('Start inspection') }}
            </progress-button>
          </li>
        </template>

        <template v-else>
          <li v-if="isLanguageSelectorEnabled" class="big:block navbar-template-builder__language-selector">
            <LanguageSelector :disabled="isWorking" @language-selected="onLanguageSelected" />
          </li>
          <li class="big:block" style="padding-top: 0.5rem">
            <progress-button
              class="mr-3 btn-secondary"
              type="button"
              :disabled="isWorking"
              :processing="submitted"
              @click.native="handleSave(0)"
            >
              {{ $t('Save as inactive') }}
            </progress-button>
          </li>
          <li class="big:block" style="padding-top: 0.5rem">
            <progress-button
              class="mr-3 is-primary"
              type="button"
              :disabled="isWorking"
              :processing="submitted"
              @click.native="handleSave(10)"
            >
              {{ $t('Save & activate') }}
            </progress-button>
          </li>
        </template>
      </ul>
    </div>
    <TranslationBanner
      v-if="isTranslationMode"
      :is-ai-translation="isAiTranslationMode"
      :is-translation-in-progress="isCurrentLangInProgress"
      class="navbar-template-builder--banner"
    />
  </div>
</template>

<script>
  import { StarredItem } from '@/ui/components/upgrade/StarredItem';
  import authMixin from '@/_legacy/mixins/authMixin';
  import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';
  import { Icon } from '@/ui/components/icon/Icon';
  import { pushRoute } from '@/ui/helpers';
  import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
  import { isAiTemplateEnabled } from '@/ui/helpers/featureHelper';
  import LanguageSelector from '@/_legacy/views/ChecklistBuilder/Settings/LanguageSelector.vue';
  import { isFormTranslationsFeatureEnabled } from '@/ui/auth/auth';
  import { useTranslationLanguagesStore } from '@/stores/checklist/translationLanguages';
  import TranslationBanner from '@/_legacy/views/ChecklistBuilder/TranslationBanner.vue';
  import { useBuilderMode } from '@/ui/concerns/checklist/useBuilderMode';

  export default {
    components: { TranslationBanner, LanguageSelector, StarredItem, Icon, ConfirmationModal },
    mixins: [authMixin],
    inject: ['isCurrentLangInProgress', 'isTranslationEnabled'],
    props: {
      onlyStartInspection: {
        type: Boolean,
        default: false,
      },
      isUpdate: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      const translationLanguagesStore = useTranslationLanguagesStore();
      const { isAiTranslationMode, isManualTranslationMode, isTranslationMode } = useBuilderMode();

      return {
        translationLanguagesStore,
        isAiTranslationMode,
        isManualTranslationMode,
        isTranslationMode,
      };
    },
    data() {
      return {
        menu: [],
        isConfirmationModalOpen: false,
      };
    },
    computed: {
      isWorking() {
        return this.submitted || this.isWordTemplateUploading;
      },
      isWordTemplateUploading() {
        return this.$store.getters['checklist_builder/isWordTemplateUploading'];
      },
      checklistStatus() {
        return this.$store.getters['checklist_builder/checklistStatus'];
      },
      activeStage() {
        return this.$store.getters['checklist_builder/activeStage'];
      },
      submitted() {
        return this.$store.getters['checklist_builder/submitted'];
      },
      isLanguageSelectorEnabled() {
        return this.isTranslationEnabled
          && this.translationLanguagesStore.hasLanguages
          && this.activeStage === 0;
      }
    },
    created() {
      this.menu = [
        {
          title: this.$t('Build'),
          componentName: 'build-stage',
        },
        {
          title: this.$t('Assign'),
          componentName: 'settings-stage',
        },
        {
          title: this.$t('Customize report'),
          componentName: 'report-stage',
          starred: !this.hasFeature('customReports'),
        },
      ];
    },
    methods: {
      changeStage(menuItemIndex) {
        if (this.isTranslationMode) {
          return;
        }

        trackClick(CLICK_EVENTS.CHANGE_STAGE, { label: this.menu[menuItemIndex].title });
        this.$store.commit('checklist_builder/activeStage', menuItemIndex);
      },
      getSaveEvent() {
        if (!this.isTranslationMode) {
          return 'save';
        }

        return this.isAiTranslationMode ? 'save-from-ai-translation' : 'save-from-manual-translation';
      },
      handleSave(status) {
        this.$emit(this.getSaveEvent(), { status });
        trackClick(CLICK_EVENTS.SAVE_CHECKLIST, { label: status === 10 ? 'Save and activate' : 'Save as inactive' });
      },
      onBackClick() {
        this.isConfirmationModalOpen = true;
      },
      onConfirmLeavePage() {
        pushRoute({ name: isAiTemplateEnabled() && !this.isUpdate ? 'checklistChooseMethod' : 'forms' });
      },
      onDeclineLeavePage() {
        this.isConfirmationModalOpen = false;
      },
      onLanguageSelected(languageId) {
        this.$emit('save', { status: this.checklistStatus, languageId });
      },
    },
  };
</script>
